"use client"

import type { TrackProperties } from "@/modules/tracking/generated-types/track"
import type { TrackEventProperties } from "@/modules/tracking/types"
import type { ComponentProps, FunctionComponent, MouseEventHandler } from "react"

import { Button } from "@jobteaser/spark/components/Button"

import { Link } from "@/modules/routing/components/Link"
import { getClientPageType } from "@/modules/routing/routingManager/clientRoutingManager"
import { useTracking } from "@/modules/tracking/useTracking"

type BaseProps =
  | { trackingProps: TrackEventProperties }
  | {
      trackingProps: {
        /**
         * If no `eventName` is defined, the current feature page will be guessed and the properties
         * will be passed to the next navigation event (aka page view).
         */
        eventName?: never
        eventProperties: TrackProperties[keyof TrackProperties]
      }
    }

type TrackButtonLinkProps = BaseProps & ComponentProps<typeof Button> & ComponentProps<typeof Link>

export const TrackButtonLink: FunctionComponent<TrackButtonLinkProps> = ({
  trackingProps: { eventName, eventProperties },
  ...props
}) => {
  const tracking = useTracking()

  const handleClick: MouseEventHandler<HTMLAnchorElement> = (event): void => {
    if (!eventName && eventProperties) {
      const linkUrl = new URL(event.currentTarget.href)

      tracking?.setRefererParams(
        {
          ...eventProperties,
          from_feature: getClientPageType() || "unknown",
        },
        { keyName: linkUrl.pathname }
      )
    }

    if (eventName) {
      tracking?.track(eventName, eventProperties)
    }

    if (props.onClick) props.onClick(event)
  }

  return <Button {...props} onClick={handleClick} renderComponent={Link} />
}
